"use client"

import * as React from "react"
import { Button } from "@/components/ui/button"
import { Plus, RefreshCw, Pentagon, Map, Circle, Pin } from "lucide-react"
import { DataTable } from "@/components/data-table/data-table"
import { DataTableFacetedFilter } from "@/components/data-table/data-table-faceted-filter"
import { getColumns } from "./data-table/columns"
import { ServiceLocationDialog } from "./ServiceLocationDialog"
import { AreaLocationDialog } from "./AreaLocationDialog"
import { RadiusLocationDialog } from "./RadiusLocationDialog"
import axiosInstance from "@/utils/axiosInstance"

export interface ServiceLocation {
    id: number
    settings_id: number
    name: string
    type: "POINT" | "AREA" | "RADIUS"
    coordinates: Array<{ lat: number; lng: number; name?: string }>
    radius?: number // in kilometers, only for RADIUS type
    description?: string
    created_at: string
    updated_at: string
}

const ServiceLocationsList = () => {
    // Local state for data loading
    const [data, setData] = React.useState<ServiceLocation[]>([])
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState<string | null>(null)

    // Local state
    const [selectedLocation, setSelectedLocation] = React.useState<ServiceLocation | null>(null)
    const [dialogOpen, setDialogOpen] = React.useState(false)
    const [dialogMode, setDialogMode] = React.useState<"create" | "edit">("create")
    const [areaDialogOpen, setAreaDialogOpen] = React.useState(false)
    const [areaDialogMode, setAreaDialogMode] = React.useState<"create" | "edit">("create")
    const [selectedAreaLocation, setSelectedAreaLocation] = React.useState<ServiceLocation | null>(null)
    const [radiusDialogOpen, setRadiusDialogOpen] = React.useState(false)
    const [radiusDialogMode, setRadiusDialogMode] = React.useState<"create" | "edit">("create")
    const [selectedRadiusLocation, setSelectedRadiusLocation] = React.useState<ServiceLocation | null>(null)

    // Data fetching function
    const fetchServiceLocations = React.useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const response = await axiosInstance.get('/route-areas')
            setData(response.data || [])
        } catch (err) {
            setError('Failed to load service locations')
            console.error('Error fetching service locations:', err)
        } finally {
            setLoading(false)
        }
    }, [])

    // Load data on component mount
    React.useEffect(() => {
        fetchServiceLocations()
    }, [fetchServiceLocations])

    const handleCreate = () => {
        setSelectedLocation(null)
        setDialogMode("create")
        setDialogOpen(true)
    }

    const handleCreateArea = () => {
        setSelectedAreaLocation(null)
        setAreaDialogMode("create")
        setAreaDialogOpen(true)
    }

    const handleCreateRadius = () => {
        setSelectedRadiusLocation(null)
        setRadiusDialogMode("create")
        setRadiusDialogOpen(true)
    }

    const handleRefresh = () => {
        fetchServiceLocations()
    }

    const handleEditClick = (location: ServiceLocation) => {
        if (location.type === "AREA") {
            setSelectedAreaLocation(location)
            setAreaDialogMode("edit")
            setAreaDialogOpen(true)
        } else if (location.type === "RADIUS") {
            setSelectedRadiusLocation(location)
            setRadiusDialogMode("edit")
            setRadiusDialogOpen(true)
        } else {
            setSelectedLocation(location)
            setDialogMode("edit")
            setDialogOpen(true)
        }
    }

    const handleDeleteSuccess = () => {
        fetchServiceLocations()
    }

    const columns = getColumns(handleEditClick, handleDeleteSuccess)

    const renderToolbarFilters = (table: any) => (
        <div className="flex items-center space-x-2">
            {table?.getColumn("type") && (
                <DataTableFacetedFilter
                    column={table.getColumn("type")}
                    title="Location Type"
                    options={[
                        {
                            label: "Point",
                            value: "POINT",
                            icon: Pin,
                        },
                        {
                            label: "Area", 
                            value: "AREA",
                            icon: Pentagon,
                        },
                        {
                            label: "Radius", 
                            value: "RADIUS",
                            icon: Circle,
                        },
                    ]}
                />
            )}
        </div>
    )

    return (
        <div className="space-y-4">
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-600">{error}</p>
                </div>
            )}

            <div className="p-6">
                <DataTable
                    data={data}
                    columns={columns}
                    renderToolbarFilters={renderToolbarFilters}
                    leftActions={
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefresh}
                            className="h-8"
                            disabled={loading}
                        >
                            <RefreshCw className={`${loading ? 'animate-spin' : ''}`} />
                        </Button>
                    }
                    rightActions={
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                onClick={handleCreate}
                                className="h-8"
                            >
                                <Plus className="mr-2 h-4 w-4" />
                                Add Location
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleCreateArea}
                                className="h-8"
                            >
                                <Pentagon className="mr-2 h-4 w-4" />
                                Draw Area
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleCreateRadius}
                                className="h-8"
                            >
                                <Circle className="mr-2 h-4 w-4" />
                                Create Radius
                            </Button>
                        </div>
                    }
                />

                <ServiceLocationDialog
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    location={selectedLocation}
                    mode={dialogMode}
                    onSuccess={() => {
                        setDialogOpen(false)
                        fetchServiceLocations()
                    }}
                />

                <AreaLocationDialog
                    open={areaDialogOpen}
                    onOpenChange={setAreaDialogOpen}
                    location={selectedAreaLocation}
                    mode={areaDialogMode}
                    onSuccess={() => {
                        setAreaDialogOpen(false)
                        fetchServiceLocations()
                    }}
                />

                <RadiusLocationDialog
                    open={radiusDialogOpen}
                    onOpenChange={setRadiusDialogOpen}
                    location={selectedRadiusLocation}
                    mode={radiusDialogMode}
                    onSuccess={() => {
                        setRadiusDialogOpen(false)
                        fetchServiceLocations()
                    }}
                />
            </div>
        </div>
    )
}

export default ServiceLocationsList